import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, LoaderCircle, Plus, Search, X } from "lucide-react";
import type { ExperimentProjection, RunProjection } from "@roboscientist/contracts";
import { EmptyState, OverflowReveal } from "@roboscientist/ui";
import { api } from "../api.js";
import { useCockpitStore } from "../store.js";
import { useThemePalette } from "../useThemePalette.js";
import { comparisonRunColor } from "./chartMath.js";
import { MetricComparisonChart, type MetricComparisonCurve } from "./MetricComparisonChart.js";
import { comparisonMetricAvailability, type ComparisonMetricAvailability } from "./metricComparison.js";

const MAX_COMPARISON_RUNS = 6;
const excludedMetrics = new Set(["elapsed_seconds", "eta_seconds"]);
const runName = (run: RunProjection) => run.id.split(":").at(-1) ?? run.id;
const compactLabel = (value: string, maximum: number) => value.length <= maximum ? value : `${value.slice(0, maximum - 1)}…`;
const activeRun = (run: RunProjection) => run.status === "active" || run.status === "running";

const availabilityLabel = (availability: ComparisonMetricAvailability, metric: string, pointCount = 0) => {
  if (availability === "available") return `${pointCount.toLocaleString()} point${pointCount === 1 ? "" : "s"}`;
  if (availability === "waiting") return "Waiting for first data point";
  return `No ${metric} result`;
};

type SelectedState = {
  kind: "available" | "loading" | "waiting" | "missing" | "error";
  detail: string;
};

export function ExperimentMetricComparison({
  experiments,
  runs
}: {
  experiments: ExperimentProjection[];
  runs: RunProjection[];
}) {
  const { isLight } = useThemePalette();
  const metric = useCockpitStore((state) => state.experimentComparisonMetric);
  const selectedRunIds = useCockpitStore((state) => state.experimentComparisonRunIds);
  const setComparison = useCockpitStore((state) => state.setExperimentComparison);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const catalog = useQuery({
    queryKey: ["metric-catalog", "training"],
    queryFn: () => api.metricCatalog("training"),
    refetchInterval: 30_000
  });

  const runById = useMemo(() => new Map(runs.map((run) => [run.id, run])), [runs]);
  const experimentById = useMemo(() => new Map(experiments.map((experiment) => [experiment.id, experiment])), [experiments]);
  const trainingRuns = useMemo(() => [...runs]
    .filter((run) => run.kind === "training")
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)), [runs]);
  const trainingRunIds = useMemo(() => new Set(trainingRuns.map((run) => run.id)), [trainingRuns]);
  const catalogByRun = useMemo(() => {
    const result = new Map<string, Map<string, number>>();
    for (const runCatalog of catalog.data?.runs ?? []) {
      if (!trainingRunIds.has(runCatalog.runId)) continue;
      const names = result.get(runCatalog.runId) ?? new Map<string, number>();
      for (const entry of runCatalog.metrics) names.set(entry.name, entry.pointCount);
      result.set(runCatalog.runId, names);
    }
    return result;
  }, [catalog.data?.runs, trainingRunIds]);
  const metricOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const names of catalogByRun.values()) {
      for (const name of names.keys()) {
        if (!excludedMetrics.has(name)) counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    for (const run of trainingRuns) {
      const name = run.primaryMetricName;
      if (name && !excludedMetrics.has(name) && !counts.has(name)) counts.set(name, 0);
    }
    return [...counts.entries()]
      .map(([name, runCount]) => ({ name, runCount }))
      .sort((a, b) => b.runCount - a.runCount || a.name.localeCompare(b.name));
  }, [catalogByRun, trainingRuns]);

  useEffect(() => {
    const availableRunIds = selectedRunIds.filter((runId) => runById.has(runId));
    if (availableRunIds.length !== selectedRunIds.length) setComparison(metric, availableRunIds);
  }, [metric, runById, selectedRunIds, setComparison]);

  const selectedRuns = selectedRunIds.map((runId) => runById.get(runId)).filter((run): run is RunProjection => Boolean(run));
  const metricQueries = useQueries({
    queries: selectedRuns.map((run) => ({
      queryKey: ["metrics", run.id, metric],
      queryFn: () => api.metrics(run.id, [metric]),
      enabled: Boolean(metric),
      refetchInterval: activeRun(run) ? 5_000 : false
    }))
  });
  const selectedStates = selectedRuns.map((run, index): SelectedState => {
    const query = metricQueries[index];
    if (query?.isPending) return { kind: "loading", detail: "Loading metric" };
    if (query?.isError) return { kind: "error", detail: "Failed to load metric" };
    const series = query?.data?.series.find((entry) => entry.name === metric);
    if (series?.points.length) return { kind: "available", detail: `${series.points.length.toLocaleString()} points` };
    const availableNames = new Set(catalogByRun.get(run.id)?.keys() ?? []);
    if (comparisonMetricAvailability(run, availableNames, metric) === "waiting") {
      return { kind: "waiting", detail: "Waiting for first data point" };
    }
    return { kind: "missing", detail: `No ${metric} result` };
  });
  const curves = selectedRuns.flatMap((run, index): MetricComparisonCurve[] => {
    const series = metricQueries[index]?.data?.series.find((entry) => entry.name === metric);
    if (!series?.points.length) return [];
    const experiment = experimentById.get(run.experimentId);
    const experimentName = experiment?.name ?? run.experimentId;
    const name = runName(run);
    return [{
      runId: run.id,
      label: `${compactLabel(experimentName, 14)} / ${compactLabel(name, 22)}`,
      fullLabel: `${experimentName} / ${name}`,
      color: comparisonRunColor(run.id, isLight),
      points: series.points
    }];
  });
  const unavailableCount = selectedStates.filter((state) => state.kind === "missing" || state.kind === "waiting" || state.kind === "error").length;
  const loadingCount = selectedStates.filter((state) => state.kind === "loading").length;

  const toggleRun = (runId: string) => {
    if (selectedRunIds.includes(runId)) {
      setComparison(metric, selectedRunIds.filter((candidate) => candidate !== runId));
      return;
    }
    if (selectedRunIds.length < MAX_COMPARISON_RUNS) setComparison(metric, [...selectedRunIds, runId]);
  };
  const clearAll = () => {
    setComparison("", []);
    setPickerOpen(false);
    setPickerSearch("");
  };

  const normalizedSearch = pickerSearch.trim().toLowerCase();
  const pickerExperiments = experiments.filter((experiment) => {
    if (!normalizedSearch) return true;
    if (`${experiment.name} ${experiment.id}`.toLowerCase().includes(normalizedSearch)) return true;
    return trainingRuns.some((run) => run.experimentId === experiment.id && `${runName(run)} ${run.id} ${run.path}`.toLowerCase().includes(normalizedSearch));
  });

  return <div className="experiment-metric-compare">
    <div className="comparison-setup-bar">
      <label>
        <span>METRIC</span>
        <select
          aria-label="Comparison metric"
          value={metric}
          disabled={catalog.isPending || catalog.isError || !metricOptions.length}
          onChange={(event) => setComparison(event.target.value, selectedRunIds)}
        >
          <option value="">{catalog.isPending ? "Loading metrics…" : metricOptions.length ? "Select a metric…" : "No indexed metrics"}</option>
          {metricOptions.map((option) => <option key={option.name} value={option.name}>{option.name} · {option.runCount} Run{option.runCount === 1 ? "" : "s"}</option>)}
        </select>
      </label>
      <button
        className="button comparison-add-button"
        disabled={!metric || catalog.isPending || catalog.isError}
        aria-expanded={pickerOpen}
        onClick={() => setPickerOpen((open) => !open)}
      >{pickerOpen ? "Done" : <><Plus size={14} /> Add experiment / Run</>}</button>
      <button className="comparison-clear-button" disabled={!metric && !selectedRunIds.length} onClick={clearAll}>Clear all</button>
      <span className="comparison-limit">{selectedRunIds.length}/{MAX_COMPARISON_RUNS} curves</span>
    </div>

    {catalog.isError ? <div className="comparison-inline-alert comparison-inline-alert--danger" role="alert"><AlertTriangle size={14} /><span>Metric catalog failed to load.</span><button onClick={() => void catalog.refetch()}>Retry</button></div> : null}

    {pickerOpen && metric ? <div className="comparison-run-picker" aria-label="Add comparison Runs">
      <label className="comparison-run-picker__search"><Search size={14} /><input aria-label="Search comparison experiments and Runs" value={pickerSearch} onChange={(event) => setPickerSearch(event.target.value)} placeholder="Search experiments or Runs" /></label>
      <div className="comparison-run-picker__list">
        {pickerExperiments.map((experiment) => {
          const candidates = trainingRuns.filter((run) => run.experimentId === experiment.id && (
            !normalizedSearch || `${experiment.name} ${experiment.id} ${runName(run)} ${run.id} ${run.path}`.toLowerCase().includes(normalizedSearch)
          ));
          const availableCount = candidates.filter((run) => catalogByRun.get(run.id)?.has(metric)).length;
          const waitingCount = candidates.filter((run) => comparisonMetricAvailability(run, new Set(catalogByRun.get(run.id)?.keys() ?? []), metric) === "waiting").length;
          return <section key={experiment.id} className="comparison-experiment-group">
            <header>
              <div><OverflowReveal as="strong" value={experiment.name} label="Experiment name" /><OverflowReveal as="code" value={experiment.id} kind="identifier" label="Experiment ID" copyable focusable /></div>
              {availableCount
                ? <span className="comparison-availability comparison-availability--available"><Check size={12} /> {availableCount} available</span>
                : waitingCount
                  ? <span className="comparison-availability comparison-availability--waiting"><LoaderCircle size={12} /> Waiting for data</span>
                  : <span className="comparison-availability comparison-availability--missing"><AlertTriangle size={12} /> No {metric} result</span>}
            </header>
            {candidates.length ? <div className="comparison-run-options" role="listbox" aria-label={`${experiment.name} comparison Runs`} aria-multiselectable="true">
              {candidates.map((run) => {
                const metrics = catalogByRun.get(run.id) ?? new Map<string, number>();
                const availableNames = new Set(metrics.keys());
                const availability = comparisonMetricAvailability(run, availableNames, metric);
                const selected = selectedRunIds.includes(run.id);
                const disabled = availability !== "available" && !selected;
                const limitReached = selectedRunIds.length >= MAX_COMPARISON_RUNS && !selected;
                return <button
                  key={run.id}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={disabled || limitReached}
                  disabled={disabled || limitReached}
                  className={selected ? "selected" : ""}
                  aria-label={`${runName(run)}, ${availabilityLabel(availability, metric, metrics.get(metric) ?? 0)}`}
                  onClick={() => toggleRun(run.id)}
                >
                  <i className={`run-kind-dot run-kind-dot--${run.kind}`} />
                  <span><OverflowReveal as="strong" value={run.id} displayValue={runName(run)} kind="identifier" label="Run ID" copyable /><small>{availabilityLabel(availability, metric, metrics.get(metric) ?? 0)}</small></span>
                  {selected ? <Check size={14} /> : availability === "waiting" ? <LoaderCircle size={14} /> : availability === "missing" ? <AlertTriangle size={14} /> : <Plus size={14} />}
                </button>;
              })}
            </div> : <div className="comparison-experiment-empty">No training Runs in this experiment.</div>}
          </section>;
        })}
        {!pickerExperiments.length ? <div className="comparison-experiment-empty">No experiments or Runs match this search.</div> : null}
      </div>
    </div> : null}

    {selectedRuns.length ? <div className="comparison-selection-tray" aria-label="Selected comparison Runs">
      {selectedRuns.map((run, index) => {
        const experiment = experimentById.get(run.experimentId);
        const state = selectedStates[index] ?? { kind: "loading", detail: "Loading metric" };
        const color = comparisonRunColor(run.id, isLight);
        return <div key={run.id} className={`comparison-selection-chip comparison-selection-chip--${state.kind}`} style={{ "--curve-color": color } as CSSProperties}>
          <i /><span><OverflowReveal as="strong" value={experiment?.name ?? run.experimentId} label="Experiment name" /><OverflowReveal as="small" value={run.id} displayValue={`${runName(run)} · ${state.detail}`} kind="identifier" label="Run ID" copyable /></span>
          <button aria-label={`Remove ${runName(run)} from comparison`} onClick={() => toggleRun(run.id)}><X size={13} /></button>
        </div>;
      })}
    </div> : null}

    {unavailableCount ? <div className="comparison-inline-alert" role="status"><AlertTriangle size={14} /><span>{unavailableCount} selected Run{unavailableCount === 1 ? " has" : "s have"} no usable {metric} result.</span></div> : null}

    {!metric
      ? <EmptyState title="Build a metric comparison" detail="Select a metric, then manually add experiment Runs. Compare starts empty and keeps your choices when you switch views." />
      : !selectedRuns.length
        ? <EmptyState title={`No ${metric} curves selected`} detail="Add one or more experiment Runs to visualize this metric." />
        : curves.length
          ? <MetricComparisonChart metric={metric} curves={curves} />
          : loadingCount
            ? <EmptyState title="Loading comparison curves" detail={`Loading ${loadingCount} selected Run${loadingCount === 1 ? "" : "s"}.`} />
            : <EmptyState title={`No comparable ${metric} result`} detail="The selected Runs have no numeric points for this metric. Choose another Run or metric." />}
  </div>;
}
